/**
 * Thermal Printer Service
 * Supports both Web USB (for HTTPS/localhost) and Browser Print (fallback)
 */

class ThermalPrinterService {
  constructor() {
    this.device = null;
    this.outEndpoint = null;
    this.encoder = new TextEncoder();
    this.printMode = 'browser'; // 'usb' or 'browser'
  }

  // ESC/POS Commands
  static ESC = 0x1B;
  static GS = 0x1D;
  static LF = 0x0A;
  static CR = 0x0D;

  // Commands
  static INIT = [0x1B, 0x40]; // Initialize printer
  static CUT = [0x1D, 0x56, 0x00]; // Full cut
  static PARTIAL_CUT = [0x1D, 0x56, 0x01]; // Partial cut
  static FEED_LINE = [0x0A]; // Line feed
  static FEED_LINES = (n) => [0x1B, 0x64, n]; // Feed n lines
  
  // Text alignment
  static ALIGN_LEFT = [0x1B, 0x61, 0x00];
  static ALIGN_CENTER = [0x1B, 0x61, 0x01];
  static ALIGN_RIGHT = [0x1B, 0x61, 0x02];
  
  // Text formatting
  static BOLD_ON = [0x1B, 0x45, 0x01];
  static BOLD_OFF = [0x1B, 0x45, 0x00];
  static DOUBLE_HEIGHT_ON = [0x1B, 0x21, 0x10];
  static DOUBLE_WIDTH_ON = [0x1B, 0x21, 0x20];
  static DOUBLE_SIZE_ON = [0x1B, 0x21, 0x30];
  static NORMAL_SIZE = [0x1B, 0x21, 0x00];
  static UNDERLINE_ON = [0x1B, 0x2D, 0x01];
  static UNDERLINE_OFF = [0x1B, 0x2D, 0x00];

  // Font sizes
  static FONT_SIZE = {
    small: [0x1B, 0x21, 0x00],
    medium: [0x1B, 0x21, 0x00],
    large: [0x1B, 0x21, 0x10]
  };

  /**
   * Check if Web USB is supported and allowed
   */
  static isUSBSupported() {
    // Check if USB API exists
    if (!('usb' in navigator)) {
      console.log('Web USB: navigator.usb not available - browser does not support Web USB');
      return false;
    }
    
    // Check if we're in a secure context
    if (window.isSecureContext === false) {
      console.log('Web USB: Not in secure context');
      return false;
    }
    
    // Web USB should work on HTTPS
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    
    console.log('Web USB check - Protocol:', protocol, 'Hostname:', hostname, 'isSecureContext:', window.isSecureContext);
    
    // If isSecureContext is true, USB should work
    if (window.isSecureContext) {
      console.log('Web USB: Secure context confirmed, USB should be available');
      return true;
    }
    
    // Fallback checks
    if (protocol === 'https:') {
      console.log('Web USB: HTTPS detected, USB should be available');
      return true;
    }
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      console.log('Web USB: localhost detected, USB should be available');
      return true;
    }
    
    console.log('Web USB: Not secure context, USB not available');
    return false;
  }
  
  /**
   * Get diagnostic info for troubleshooting
   */
  static getDiagnostics() {
    return {
      hasUSBApi: 'usb' in navigator,
      isSecureContext: window.isSecureContext,
      protocol: window.location.protocol,
      hostname: window.location.hostname,
      userAgent: navigator.userAgent
    };
  }

  /**
   * Check if any printing method is available
   */
  static isSupported() {
    return true; // Browser print is always available
  }

  /**
   * Get the current print mode
   */
  getPrintMode() {
    return this.printMode;
  }

  /**
   * Set the print mode
   */
  setPrintMode(mode) {
    this.printMode = mode;
  }

  /**
   * Get list of paired devices (previously connected printers)
   */
  async getPairedDevices() {
    if (!ThermalPrinterService.isSupported()) {
      return [];
    }
    try {
      const devices = await navigator.usb.getDevices();
      return devices.map(d => ({
        productName: d.productName || 'USB Device',
        vendorId: d.vendorId,
        productId: d.productId
      }));
    } catch (error) {
      console.error('Error getting paired devices:', error);
      return [];
    }
  }

  /**
   * Try to reconnect to a previously paired device
   */
  async reconnectPairedDevice() {
    if (!ThermalPrinterService.isSupported()) {
      return null;
    }
    
    try {
      const devices = await navigator.usb.getDevices();
      if (devices.length > 0) {
        // Try to connect to the first paired device
        this.device = devices[0];
        await this.device.open();
        
        if (this.device.configuration === null) {
          await this.device.selectConfiguration(1);
        }
        
        // Claim interface
        for (const iface of this.device.configuration.interfaces) {
          try {
            await this.device.claimInterface(iface.interfaceNumber);
            
            // Find OUT endpoint
            for (const alternate of iface.alternates) {
              for (const endpoint of alternate.endpoints) {
                if (endpoint.direction === 'out') {
                  this.outEndpoint = endpoint.endpointNumber;
                  break;
                }
              }
            }
            break;
          } catch (e) {
            // Try next interface
          }
        }
        
        return {
          success: true,
          deviceName: this.device.productName || 'USB Printer',
          vendorId: this.device.vendorId,
          productId: this.device.productId
        };
      }
    } catch (error) {
      console.error('Error reconnecting to paired device:', error);
    }
    return null;
  }

  /**
   * Request access to a USB printer
   */
  async requestDevice() {
    if (!ThermalPrinterService.isSupported()) {
      throw new Error('Web USB is not supported in this browser. Please use Chrome or Edge.');
    }

    try {
      // Request any USB device - thermal printers typically use bulk transfer
      // Common thermal printer vendor IDs: 0x0483 (STM), 0x0416, 0x0456, 0x04b8 (Epson), 0x0519
      this.device = await navigator.usb.requestDevice({
        filters: [] // Empty filters to show all USB devices
      });
      
      console.log('Selected device:', this.device.productName, 'Vendor:', this.device.vendorId, 'Product:', this.device.productId);
      
      try {
        await this.device.open();
        console.log('Device opened');
      } catch (openError) {
        console.error('Failed to open device:', openError);
        if (openError.name === 'SecurityError') {
          throw new Error('Cannot access printer - it is being used by another application (like Windows Print Spooler). Please: 1) Close any POS/printing software, 2) Disable the printer in Device Manager, or 3) Use Browser Print mode instead.');
        }
        throw openError;
      }
      
      // Select configuration if needed
      if (this.device.configuration === null) {
        await this.device.selectConfiguration(1);
        console.log('Configuration selected');
      }
      
      // Find and claim the correct interface
      let interfaceClaimed = false;
      for (const iface of this.device.configuration.interfaces) {
        try {
          await this.device.claimInterface(iface.interfaceNumber);
          console.log('Claimed interface:', iface.interfaceNumber);
          interfaceClaimed = true;
          
          // Find OUT endpoint
          for (const alternate of iface.alternates) {
            for (const endpoint of alternate.endpoints) {
              if (endpoint.direction === 'out') {
                this.outEndpoint = endpoint.endpointNumber;
                console.log('Found OUT endpoint:', this.outEndpoint);
                break;
              }
            }
          }
          break; // Use first successfully claimed interface
        } catch (claimError) {
          console.warn('Could not claim interface', iface.interfaceNumber, ':', claimError.message);
          // Try next interface
        }
      }
      
      if (!interfaceClaimed) {
        throw new Error('Could not claim any interface. The printer might be in use by another application.');
      }
      
      console.log('Printer connected successfully:', this.device.productName);
      return {
        success: true,
        deviceName: this.device.productName || 'USB Printer',
        vendorId: this.device.vendorId,
        productId: this.device.productId
      };
    } catch (error) {
      console.error('Error connecting to printer:', error);
      
      // Clean up on error
      if (this.device) {
        try {
          await this.device.close();
        } catch (closeError) {
          // Ignore close errors
        }
        this.device = null;
      }
      
      // Provide more helpful error messages
      if (error.name === 'NotFoundError') {
        throw new Error('No printer selected. Please select a printer from the dialog.');
      } else if (error.name === 'SecurityError') {
        // Check the actual reason
        const protocol = window.location.protocol;
        if (protocol !== 'https:' && window.location.hostname !== 'localhost') {
          throw new Error('USB access denied. This feature requires HTTPS or localhost.');
        } else {
          throw new Error('USB access denied by browser. Please check: 1) Site permissions for USB, 2) Browser settings allow USB access, 3) Try refreshing the page.');
        }
      } else if (error.name === 'NetworkError') {
        throw new Error('The printer is already in use by another application. Please close other apps using the printer.');
      } else if (error.name === 'InvalidStateError') {
        throw new Error('Printer connection failed. Please unplug and replug the printer, then try again.');
      } else {
        throw new Error(`Printer error: ${error.message || error.name}`);
      }
    }
  }

  /**
   * Disconnect from the printer
   */
  async disconnect() {
    if (this.device) {
      try {
        // Release interfaces first
        for (const iface of this.device.configuration.interfaces) {
          try {
            await this.device.releaseInterface(iface.interfaceNumber);
          } catch (e) {
            // Ignore release errors
          }
        }
        await this.device.close();
        console.log('Printer disconnected');
      } catch (error) {
        console.error('Error disconnecting printer:', error);
      } finally {
        this.device = null;
        this.outEndpoint = null;
      }
    }
  }

  /**
   * Send raw data to printer
   */
  async sendRaw(data) {
    if (!this.device) {
      throw new Error('No printer connected. Please connect a printer first.');
    }

    try {
      // Use stored endpoint or find it
      let endpointNumber = this.outEndpoint;
      
      if (!endpointNumber) {
        // Try to find OUT endpoint
        for (const iface of this.device.configuration.interfaces) {
          for (const alternate of iface.alternates) {
            for (const endpoint of alternate.endpoints) {
              if (endpoint.direction === 'out') {
                endpointNumber = endpoint.endpointNumber;
                this.outEndpoint = endpointNumber;
                break;
              }
            }
            if (endpointNumber) break;
          }
          if (endpointNumber) break;
        }
      }

      if (!endpointNumber) {
        throw new Error('No OUT endpoint found on the printer');
      }

      const dataArray = data instanceof Uint8Array ? data : new Uint8Array(data);
      await this.device.transferOut(endpointNumber, dataArray);
    } catch (error) {
      console.error('Error sending data to printer:', error);
      
      // If transfer fails, printer might be disconnected
      if (error.name === 'NetworkError' || error.name === 'NotFoundError') {
        this.device = null;
        this.outEndpoint = null;
        throw new Error('Printer disconnected. Please reconnect the printer.');
      }
      throw error;
    }
  }

  /**
   * Send text to printer (USB mode only)
   */
  async sendText(text) {
    const encoded = this.encoder.encode(text);
    await this.sendRaw(encoded);
  }

  /**
   * Print a receipt - automatically chooses USB or Browser mode
   */
  async printReceipt(receiptData, settings = {}) {
    // Use browser print mode if USB is not available or not connected
    if (this.printMode === 'browser' || !this.device) {
      return this.printReceiptBrowser(receiptData, settings);
    }
    
    // Use USB/ESC-POS mode
    return this.printReceiptUSB(receiptData, settings);
  }

  /**
   * Print receipt using browser's print dialog (works everywhere)
   */
  async printReceiptBrowser(receiptData, settings = {}) {
    const {
      paper_width = '80mm',
      font_size = 'medium',
      copies = 1,
      header_text = 'Welcome to 1946',
      footer_text = 'Thank you for your visit!',
      show_franchise_name = true,
      show_franchise_address = true,
      show_franchise_contact = true,
      show_date_time = true,
      show_order_number = true,
      show_item_details = true,
      show_payment_method = true,
      show_total = true
    } = settings;

    // Paper width in mm
    const paperWidthMM = paper_width === '58mm' ? 58 : 80;
    
    // Font sizes
    const fontSizes = {
      small: { body: '10px', header: '14px', total: '16px' },
      medium: { body: '12px', header: '16px', total: '20px' },
      large: { body: '14px', header: '20px', total: '24px' }
    };
    const fonts = fontSizes[font_size] || fontSizes.medium;

    // Format date
    let dateStr = '';
    let timeStr = '';
    if (receiptData.date_timestamp) {
      const date = new Date(receiptData.date_timestamp);
      dateStr = date.toLocaleDateString('en-IN');
      timeStr = date.toLocaleTimeString('en-IN');
    }

    // Build receipt HTML
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Receipt - ${receiptData.order_no}</title>
        <style>
          @page {
            size: ${paperWidthMM}mm auto;
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', monospace;
            font-size: ${fonts.body};
            width: ${paperWidthMM}mm;
            padding: 2mm;
            background: white;
            color: black;
          }
          .center { text-align: center; }
          .left { text-align: left; }
          .right { text-align: right; }
          .bold { font-weight: bold; }
          .header { font-size: ${fonts.header}; font-weight: bold; margin-bottom: 2mm; }
          .brand { font-size: 24px; font-weight: bold; letter-spacing: 2px; }
          .divider { border-top: 1px dashed #000; margin: 2mm 0; }
          .double-divider { border-top: 2px solid #000; margin: 2mm 0; }
          .item-row { display: flex; justify-content: space-between; margin: 1mm 0; }
          .item-name { flex: 1; }
          .item-qty { width: 30px; text-align: center; }
          .item-price { width: 60px; text-align: right; }
          .total-row { display: flex; justify-content: space-between; font-size: ${fonts.total}; font-weight: bold; margin: 2mm 0; }
          .footer { margin-top: 4mm; font-size: 10px; }
          .spacer { height: 2mm; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="center">
          <div class="brand">1946</div>
          <div>Filter Coffee & Tea</div>
          ${header_text ? `<div class="spacer"></div><div>${header_text}</div>` : ''}
        </div>
        
        <div class="divider"></div>
        
        ${show_franchise_name && receiptData.franchise_name ? `<div class="center bold">${receiptData.franchise_name}</div>` : ''}
        ${show_franchise_address && receiptData.franchise_address ? `<div class="center">${receiptData.franchise_address}</div>` : ''}
        ${show_franchise_contact && receiptData.franchise_contact ? `<div class="center">Tel: ${receiptData.franchise_contact}</div>` : ''}
        
        <div class="double-divider"></div>
        
        ${show_order_number ? `<div class="bold">Order: ${receiptData.order_no}</div>` : ''}
        ${show_date_time ? `<div>Date: ${dateStr}</div><div>Time: ${timeStr}</div>` : ''}
        ${show_payment_method ? `<div>Payment: ${receiptData.payment_method}</div>` : ''}
        
        <div class="divider"></div>
        
        ${show_item_details && receiptData.items ? `
          <div class="item-row bold">
            <span class="item-name">Item</span>
            <span class="item-qty">Qty</span>
            <span class="item-price">Amount</span>
          </div>
          <div class="divider"></div>
          ${receiptData.items.map(item => `
            <div class="item-row">
              <span class="item-name">${item.name}</span>
              <span class="item-qty">${item.quantity}</span>
              <span class="item-price">₹${item.total.toFixed(2)}</span>
            </div>
          `).join('')}
        ` : ''}
        
        <div class="double-divider"></div>
        
        ${show_total ? `
          <div class="total-row">
            <span>TOTAL</span>
            <span>₹${receiptData.total_amount.toFixed(2)}</span>
          </div>
        ` : ''}
        
        <div class="divider"></div>
        
        <div class="footer center">
          ${footer_text ? `<div>${footer_text}</div>` : ''}
          <div class="spacer"></div>
          <div>Est. 1946</div>
        </div>
      </body>
      </html>
    `;

    // Create a hidden iframe for printing
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);

    // Write content to iframe
    const printDocument = printFrame.contentWindow.document;
    printDocument.open();
    printDocument.write(receiptHTML);
    printDocument.close();

    // Wait for content to load then print
    return new Promise((resolve) => {
      printFrame.onload = () => {
        setTimeout(() => {
          // Print the specified number of copies
          for (let i = 0; i < copies; i++) {
            printFrame.contentWindow.print();
          }
          
          // Clean up after a short delay
          setTimeout(() => {
            document.body.removeChild(printFrame);
            resolve({ success: true, message: `Print dialog opened for ${copies} copy/copies` });
          }, 1000);
        }, 250);
      };
    });
  }

  /**
   * Print receipt using USB/ESC-POS commands
   */
  async printReceiptUSB(receiptData, settings = {}) {
    const {
      paper_width = '80mm',
      font_size = 'medium',
      copies = 1,
      header_text = 'Welcome to 1946',
      footer_text = 'Thank you for your visit!',
      show_logo = true,
      show_franchise_name = true,
      show_franchise_address = true,
      show_franchise_contact = true,
      show_date_time = true,
      show_order_number = true,
      show_item_details = true,
      show_payment_method = true,
      show_total = true
    } = settings;

    // Character width based on paper
    const charWidth = paper_width === '58mm' ? 32 : 48;

    for (let copy = 0; copy < copies; copy++) {
      // Initialize printer
      await this.sendRaw(ThermalPrinterService.INIT);

      // Set font size
      await this.sendRaw(ThermalPrinterService.FONT_SIZE[font_size] || ThermalPrinterService.FONT_SIZE.medium);

      // Header
      await this.sendRaw(ThermalPrinterService.ALIGN_CENTER);
      await this.sendRaw(ThermalPrinterService.BOLD_ON);
      await this.sendRaw(ThermalPrinterService.DOUBLE_SIZE_ON);
      await this.sendText('1946\n');
      await this.sendRaw(ThermalPrinterService.NORMAL_SIZE);
      await this.sendRaw(ThermalPrinterService.BOLD_OFF);

      if (header_text) {
        await this.sendText(header_text + '\n');
      }

      // Franchise details
      if (show_franchise_name && receiptData.franchise_name) {
        await this.sendText(receiptData.franchise_name + '\n');
      }
      if (show_franchise_address && receiptData.franchise_address) {
        await this.sendText(receiptData.franchise_address + '\n');
      }
      if (show_franchise_contact && receiptData.franchise_contact) {
        await this.sendText('Tel: ' + receiptData.franchise_contact + '\n');
      }

      // Separator
      await this.sendText('='.repeat(charWidth) + '\n');

      // Order details
      await this.sendRaw(ThermalPrinterService.ALIGN_LEFT);
      
      if (show_order_number) {
        await this.sendRaw(ThermalPrinterService.BOLD_ON);
        await this.sendText('Order: ' + receiptData.order_no + '\n');
        await this.sendRaw(ThermalPrinterService.BOLD_OFF);
      }

      if (show_date_time && receiptData.date_timestamp) {
        const date = new Date(receiptData.date_timestamp);
        await this.sendText('Date: ' + date.toLocaleDateString('en-IN') + '\n');
        await this.sendText('Time: ' + date.toLocaleTimeString('en-IN') + '\n');
      }

      if (show_payment_method) {
        await this.sendText('Payment: ' + receiptData.payment_method + '\n');
      }

      // Items separator
      await this.sendText('-'.repeat(charWidth) + '\n');

      // Items header
      if (show_item_details && receiptData.items && receiptData.items.length > 0) {
        await this.sendRaw(ThermalPrinterService.BOLD_ON);
        const headerLine = this.formatLine('Item', 'Qty', 'Amount', charWidth);
        await this.sendText(headerLine + '\n');
        await this.sendRaw(ThermalPrinterService.BOLD_OFF);
        await this.sendText('-'.repeat(charWidth) + '\n');

        // Items
        for (const item of receiptData.items) {
          const itemName = item.name.length > (charWidth - 20) 
            ? item.name.substring(0, charWidth - 23) + '...' 
            : item.name;
          
          await this.sendText(itemName + '\n');
          const priceLine = this.formatLine(
            `  Rs.${item.price.toFixed(2)} x ${item.quantity}`,
            '',
            `Rs.${item.total.toFixed(2)}`,
            charWidth
          );
          await this.sendText(priceLine + '\n');
        }
      }

      // Total separator
      await this.sendText('='.repeat(charWidth) + '\n');

      // Total
      if (show_total) {
        await this.sendRaw(ThermalPrinterService.BOLD_ON);
        await this.sendRaw(ThermalPrinterService.DOUBLE_HEIGHT_ON);
        const totalLine = this.formatLine('TOTAL', '', `Rs.${receiptData.total_amount.toFixed(2)}`, charWidth);
        await this.sendText(totalLine + '\n');
        await this.sendRaw(ThermalPrinterService.NORMAL_SIZE);
        await this.sendRaw(ThermalPrinterService.BOLD_OFF);
      }

      // Footer
      await this.sendRaw(ThermalPrinterService.FEED_LINES(1));
      await this.sendRaw(ThermalPrinterService.ALIGN_CENTER);
      
      if (footer_text) {
        await this.sendText(footer_text + '\n');
      }
      
      await this.sendText('Est. 1946\n');

      // Feed and cut
      await this.sendRaw(ThermalPrinterService.FEED_LINES(3));
      await this.sendRaw(ThermalPrinterService.PARTIAL_CUT);

      // Small delay between copies
      if (copy < copies - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    return { success: true, message: `Printed ${copies} copy/copies` };
  }

  /**
   * Format a line with left, center, and right aligned text
   */
  formatLine(left, center, right, totalWidth) {
    left = left || '';
    center = center || '';
    right = right || '';

    const leftLen = left.length;
    const centerLen = center.length;
    const rightLen = right.length;

    if (center) {
      // Three-column layout
      const spaces1 = Math.max(1, Math.floor((totalWidth - leftLen - centerLen - rightLen) / 2));
      const spaces2 = totalWidth - leftLen - centerLen - rightLen - spaces1;
      return left + ' '.repeat(spaces1) + center + ' '.repeat(Math.max(1, spaces2)) + right;
    } else {
      // Two-column layout (left and right)
      const spaces = totalWidth - leftLen - rightLen;
      return left + ' '.repeat(Math.max(1, spaces)) + right;
    }
  }

  /**
   * Test print
   */
  async testPrint(settings = {}) {
    const testReceipt = {
      order_no: 'TEST-001',
      date_timestamp: new Date().toISOString(),
      franchise_name: '1946 Filter Coffee & Tea',
      franchise_address: 'Test Address',
      franchise_contact: '1234567890',
      items: [
        { name: 'Filter Coffee', price: 15, quantity: 2, total: 30 },
        { name: 'Tea', price: 10, quantity: 1, total: 10 }
      ],
      total_amount: 40,
      payment_method: 'Cash'
    };

    return await this.printReceipt(testReceipt, settings);
  }
}

// Singleton instance
const thermalPrinter = new ThermalPrinterService();

export default thermalPrinter;
export { ThermalPrinterService };
